import { TableWidgetData, WidgetType } from '@/models/PanelWidgets';
import { UpdateTableWidgetResponseBody } from '@/api/ApiResponse';

import { v4 as uuidV4 } from 'uuid';

export type TableRowId = string;
export type TableColumnId = string;
export interface TableColumn {
  id: TableColumnId;
  name: string;
  description: string;
  tools: string[];
  isMetadata?: boolean;
}

export interface NewTableColumn {
  name: string;
  description: string;
  isMetadata?: boolean;
}

export interface TableRow {
  id: TableRowId;
  paperCorpusId?: number;
  displayValue?: string;
  isSelected: boolean;
}

export interface TableCell {
  isLoading: boolean;
  displayValue: string | null;
  error?: string | null;
  metadata?: { evidenceId?: number | undefined };
  editedValue?: string | null;
  editedByUUID?: string | null;
}

export interface TableRecommendation {
  corpusId: number;
  displayValue?: string;
}

export const tableCellFactory = ({
  isLoading,
  displayValue,
  error,
  metadata,
  editedValue,
  editedByUUID,
}: {
  isLoading?: boolean;
  displayValue?: string | null;
  error?: string;
  metadata?: { evidenceId?: number | undefined };
  editedValue?: string | null;
  editedByUUID?: string | null;
} = {}): TableCell => {
  return {
    isLoading: isLoading ?? false,
    displayValue: displayValue ?? null,
    metadata,
    editedValue,
    editedByUUID,
    ...(error && { error }),
  };
};

export const tableRowFactory = ({
  id,
  paperCorpusId,
  displayValue,
  isSelected,
}: {
  id?: TableRowId;
  paperCorpusId?: number;
  displayValue?: string;
  isSelected?: boolean;
}): TableRow => {
  const newRow: TableRow = {
    id: id ?? uuidV4(),
    paperCorpusId: paperCorpusId,
    displayValue: displayValue ?? `CorpusId:${paperCorpusId}`,
    isSelected: isSelected ?? false,
  };
  return newRow;
};

export const tableColumnFactory = ({
  id,
  name,
  description,
  tools,
  isMetadata,
}: {
  id?: TableColumnId;
  name?: string;
  description?: string;
  tools?: string[];
  isMetadata?: boolean;
}): TableColumn => {
  return {
    id: id ?? uuidV4(),
    name: name ?? `ColumnId:${id}`,
    description: description ?? `Description for ${id}`,
    tools: tools ?? [],
    isMetadata: isMetadata ?? false,
  };
};

export const tableWidgetFactory = ({
  title,
  id,
  rows,
  columns,
  cells,
  isLoading,
  widgetType,
}: {
  title?: string;
  id?: string;
  rows?: TableRow[];
  columns?: TableColumn[];
  cells?: TableCellRecord;
  isLoading?: boolean;
  widgetType?: WidgetType.TABLE | WidgetType.TABLE_RECOMMENDATIONS;
} = {}): TableWidgetData => {
  return {
    ...(id && { id }),
    type: widgetType || WidgetType.TABLE,
    title: title ?? '',
    rows: rows ?? [],
    columns: columns ?? [],
    cells: cells ?? {},
    isLoading: isLoading ?? false,
  };
};

export type TableCellRecord = Record<string, TableCell>;

// keep these models in sync with the nora-widget-service table.py models
export type TableColumnFromApi = {
  id: string;
  name: string;
  description: string;
  tools: string[];
  is_metadata?: boolean;
};

export type TableRowFromApi = {
  id: string;
  display_value?: string;
  paper_corpus_id?: number;
};

export type TableCellFromApi = {
  id: string;
  value?: string | number;
  display_value: string;
  is_loading: boolean;
  error?: string;
  metadata?: Record<string, unknown>;
  edited_value?: string | null;
  edited_by_uuid?: string | null;
};

export type TableCellRecordFromApi = Record<string, TableCellFromApi>;

export type TableWidgetDataFromApi = {
  title?: string;
  id: string;
  columns: TableColumnFromApi[];
  rows: TableRowFromApi[];
  cells: TableCellRecordFromApi;
};

export const getTableColumnFromApi = (
  column: TableColumnFromApi,
): TableColumn => {
  return {
    id: column.id,
    name: column.name,
    description: column.description,
    tools: column.tools,
    isMetadata: column.is_metadata,
  };
};

export function getTableWidgetDataFromApi(
  args: TableWidgetDataFromApi,
): TableWidgetData {
  return {
    type: WidgetType.TABLE,
    title: args.title || '',
    id: args.id,
    columns: args.columns.map((column) => getTableColumnFromApi(column)),
    rows: args.rows.map((row) =>
      tableRowFactory({
        id: row.id,
        paperCorpusId: row.paper_corpus_id,
        displayValue: row.display_value,
      }),
    ),
    cells: Object.fromEntries(
      Object.entries(args.cells).map(([key, cell]) => [
        key,
        tableCellFactory({
          displayValue: cell.display_value ?? cell.value,
          error: cell.error,
          metadata: cell.metadata,
          editedValue: cell.edited_value,
          editedByUUID: cell.edited_by_uuid,
        }),
      ]),
    ),
    isLoading: false,
  };
}

export type UpdatedTableResponse = {
  tableId: string;
  eventId: string;
};

export function getUpdatedTableResponseFromApi(
  args: UpdateTableWidgetResponseBody,
): UpdatedTableResponse {
  return {
    tableId: args.table_id,
    eventId: args.event_id,
  };
}
